import {
    takeEvery,
    all,
    put,
    select,
    call
} from 'redux-saga/effects';

import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';

import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import queryString from 'query-string';
import urlJoin from 'url-join';
import fp from 'lodash/fp';
import * as actions from './actions';
import * as selectors from './selectors';
import * as staticDataIsdaApi from '../../api/staticDataIsdaApi';
import { searchToFilters, filtersToSearch } from '../../common/urlUtils';
import * as routerSelectors from '../../common/router/selectors';

import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';

const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/staticdata/staticdataisda');

export const filterHeaderActions = actionFactory('StaticDataIsda');

export function* filterChanged(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, 'StaticDataIsda'));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'StaticDataIsda');
            const search = filtersToSearch(filters);
            const sp = queryString.parse(search);
            let force = false;
            if (action && (
                action.type === actions.STATIC_DATA_ISDA_LOADED
            )) {
                sp.force = true;
                force = true;
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            const currentSearch = yield select(routerSelectors.getSearch);
            if (force || !currentSearch) {
                yield put(replace(`${PAGE_ROUTE}${newSearch}`));
            } else if (currentSearch !== newSearch) {
                yield put(push(`${PAGE_ROUTE}${newSearch}`));
            }
        }
    }
}

export function* staticDataIsdaLoaded(action) {
    try {
        const initialised = yield (select(selectors.getInitialised));
        if (!initialised) {
            yield put(actions.setLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            yield put(filterHeaderActions.setDefaultFilters(defaultFilters));
            if (action.search === '') {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(action.search, filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield put(actions.setInitialised(true));
            yield call(filterChanged, action);
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* fetchAvailableListValue(api, action) {
    try {
        const availableValues = yield call(
            api.getAvailableFilterValues,
            action.availableListValuesLocation
        );

        yield put(filterHeaderActions.setAvailableListValues(action.filterId, availableValues));
    } catch (e) {
        yield put(actions.fetchFilterListValuesError(`There was a problem fetching the values for the Prod Class filter ${e.toString()}`));
    }
}

export function* fetchProductClass(api, serverFilters) {
    const selectedProductClass = yield select(selectors.getDisplayedProductClasses);
    try {
        const currentData = yield select(selectors.getProductClassData);
        if (!currentData) {
            yield put(actions.fetchProductClassRequest(selectedProductClass));

            const data = selectedProductClass ? yield call(
                api.fetchProductClass,
                selectedProductClass
            ) : null;

            yield put(actions.fetchProductClassSuccess(selectedProductClass, data, serverFilters));
        }
    } catch (e) {
        yield put(actions.fetchProductClassError(selectedProductClass, e.toString()));
    }
}

export function* handleLocationChanged(api, action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        try {
            if (action.payload.location.search === '') {
                // redirect to the page without query params,
                // probably from the navbar, so need to rebuild the url
                yield call(filterChanged);
            } else {
                const filterDefinitions = yield select(selectors.getFilterDefinitions);
                const defaultFilters = yield select(selectors.getDefaultFilters);
                const filters = searchToFilters(action.payload.location.search,
                    filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
                const params = queryString.parse(action.payload.location.search);
                const force = params.force === 'true';

                const requestedServerFilters = fp.flow(
                    fp.filter(filter => filter.value && filter.definition.server),
                    fp.sortBy(filter => filter.definition.id),
                    JSON.stringify
                )(filters);
                const currentServerFilters = yield select(selectors.getCurrentServerFilters);
                if (force || (currentServerFilters !== requestedServerFilters)) {
                    yield call(fetchProductClass, api, requestedServerFilters);
                }
            }
        } catch (error) {
            yield put(actions.fetchProductClassError(error.toString()));
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.STATIC_DATA_ISDA_LOADED, staticDataIsdaLoaded),
        takeEvery(
            filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
            fetchAvailableListValue,
            staticDataIsdaApi
        ),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], filterChanged),
        takeEvery(
            action => action.type === LOCATION_CHANGE
            && action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase(),
            handleLocationChanged,
            staticDataIsdaApi
        )
    ]);
}
